import { create } from "zustand";
import {
  DEFAULT_SCREEN_PROFILE,
  SCREEN_CODECS,
  SCREEN_PROFILES,
  type ScreenCodec,
  type ScreenProfile,
} from "@entities/call";

/*
 * Что и как показываем: качество и кодек.
 *
 * Держится между показами — менять их каждый раз незачем, показывают обычно
 * одно и то же и с тем же результатом.
 *
 * Стором, а не состоянием при кнопке, потому что читателей теперь трое:
 * кнопка в полосе голосового канала, кнопка в панели идущего звонка и шапка
 * самого канала, где выбранное качество написано словами. Пока значение
 * жило внутри экрана канала, каждый читал своё, и поменявший качество в
 * одном месте не находил его в другом.
 *
 * Причина, по которой это стор, а не свой список подписчиков, записана в
 * шапке `shared/media/volume.ts`.
 */
const PROFILE_KEY = "airel.screen-profile";
const CODEC_KEY = "airel.screen-codec";
const SOUND_KEY = "airel.screen-sound";

interface Prefs {
  profile: ScreenProfile;
  codec: ScreenCodec;
  /**
   * Чей звук показывать — имя приложения (Linux-оболочка). Именем, а не
   * номером процесса: игру перезапустят, номер сменится, а выбор тот же.
   * `null` — без звука.
   */
  sound: string | null;
}

function readCodec(): ScreenCodec {
  const saved = localStorage.getItem(CODEC_KEY);
  return SCREEN_CODECS.some((item) => item.id === saved) ? (saved as ScreenCodec) : "h264";
}

function readProfile(): ScreenProfile {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return DEFAULT_SCREEN_PROFILE;

    const saved = JSON.parse(raw) as Partial<ScreenProfile>;
    return (
      SCREEN_PROFILES.find(
        (item) => item.height === saved.height && item.fps === saved.fps,
      ) ?? DEFAULT_SCREEN_PROFILE
    );
  } catch {
    return DEFAULT_SCREEN_PROFILE;
  }
}

export const useScreenPrefs = create<Prefs>()(() => ({
  profile: readProfile(),
  codec: readCodec(),
  sound: localStorage.getItem(SOUND_KEY),
}));

export function chooseProfile(profile: ScreenProfile) {
  useScreenPrefs.setState({ profile });
  localStorage.setItem(PROFILE_KEY, JSON.stringify({ height: profile.height, fps: profile.fps }));
}

export function chooseCodec(codec: ScreenCodec) {
  useScreenPrefs.setState({ codec });
  localStorage.setItem(CODEC_KEY, codec);
}

export function chooseSound(sound: string | null) {
  useScreenPrefs.setState({ sound });
  if (sound) localStorage.setItem(SOUND_KEY, sound);
  else localStorage.removeItem(SOUND_KEY);
}
